import React from 'react';

import {
  GeneratedReportsMetaDocument,
  GeneratedReportsMetaQueryVariables,
  InboxItemDocument,
  InboxItemQueryVariables,
  InboxItemsScrollableListDocument,
  InboxItemsScrollableListQueryVariables,
  useDeclineVerificationRequestByVerifierMutation,
  VerificationRequestDocument,
  VerificationRequestPaymentInfoDocument,
  VerificationRequestPaymentInfoQueryVariables,
  VerificationRequestQueryVariables,
} from 'src/api';
import { useNotification } from 'src/hooks/app';
import { useVerificationRequestProcessing } from 'src/hooks/verificationRequest';

import { ApproveOrDeclineVerificationRequestModalContent } from '../ApproveOrDeclineVerificationRequestModalContent';

type DeclineVerificationRequestByVerifierModalContentProps = {
  onCloseModal: () => void;
  switchClosability: (closable: boolean) => void;
  inboxItemId: string;
};

export const DeclineVerificationRequestByVerifierModalContent: React.FC<
  DeclineVerificationRequestByVerifierModalContentProps
> = ({ onCloseModal, switchClosability, inboxItemId }) => {
  const { createApolloContext } = useNotification();

  const { isVerifierResponding, startVerifierResponding, stopVerifierResponding } =
    useVerificationRequestProcessing();
  const isVerifierResponseInProgress = isVerifierResponding(inboxItemId);

  const refetchQueries = React.useMemo(() => {
    return [
      {
        query: InboxItemDocument,
        variables: { id: inboxItemId } satisfies InboxItemQueryVariables,
      },
      {
        query: InboxItemsScrollableListDocument,
        variables: {
          where: { id: { equals: inboxItemId } },
        } satisfies InboxItemsScrollableListQueryVariables,
      },
      {
        query: VerificationRequestDocument,
        variables: { filter: { inboxItemId } } satisfies VerificationRequestQueryVariables,
      },
      {
        query: VerificationRequestPaymentInfoDocument,
        variables: {
          where: { inboxItemId },
        } satisfies VerificationRequestPaymentInfoQueryVariables,
      },
      {
        query: GeneratedReportsMetaDocument,
        variables: { inboxItemId } satisfies GeneratedReportsMetaQueryVariables,
      },
    ];
  }, [inboxItemId]);

  const [declineVerificationRequest] = useDeclineVerificationRequestByVerifierMutation({
    refetchQueries,
    context: createApolloContext({
      errorMessage: 'Failed to decline verification request',
    }),
  });

  const handleAction = React.useCallback(async () => {
    switchClosability(false);
    startVerifierResponding(inboxItemId);

    await declineVerificationRequest({ variables: { inboxItemId } });

    stopVerifierResponding(inboxItemId);
    switchClosability(true);
    onCloseModal();
  }, [
    declineVerificationRequest,
    inboxItemId,
    onCloseModal,
    startVerifierResponding,
    stopVerifierResponding,
    switchClosability,
  ]);

  return (
    <ApproveOrDeclineVerificationRequestModalContent
      action="decline"
      onCloseModal={onCloseModal}
      handleAction={handleAction}
      disabled={isVerifierResponseInProgress}
      loading={isVerifierResponseInProgress}
    />
  );
};
